import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import marketingService from '../services/marketingService';
import PromotionForm from '../components/PromotionForm';

export default function Promotions() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [activeOnly, setActiveOnly] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadPromotions();
  }, [activeOnly]);

  const loadPromotions = async () => {
    try {
      setLoading(true);
      const data = await marketingService.getPromotions(activeOnly);
      setPromotions(data.promotions);
    } catch (error) {
      showAlert('Failed to load promotions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (promotion = null) => {
    setSelectedPromotion(promotion);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedPromotion(null);
  };

  const handleSavePromotion = async (promotionData) => {
    try {
      if (selectedPromotion) {
        await marketingService.updatePromotion(selectedPromotion.id, promotionData);
        showAlert('Promotion updated successfully');
      } else {
        await marketingService.createPromotion(promotionData);
        showAlert('Promotion created successfully');
      }
      handleCloseForm();
      loadPromotions();
    } catch (error) {
      showAlert('Failed to save promotion', 'error');
    }
  };

  const handleDeletePromotion = async (promotion) => {
    if (!window.confirm(`Delete promotion "${promotion.name}"?`)) return;

    try {
      await marketingService.deletePromotion(promotion.id);
      showAlert('Promotion deleted successfully');
      loadPromotions();
    } catch (error) {
      showAlert('Failed to delete promotion', 'error');
    }
  };

  const handleToggleActive = async (promotion) => {
    try {
      await marketingService.updatePromotion(promotion.id, {
        active: !promotion.active
      });
      showAlert(`Promotion ${!promotion.active ? 'activated' : 'deactivated'}`);
      loadPromotions();
    } catch (error) {
      showAlert('Failed to update promotion', 'error');
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    showAlert('Code copied to clipboard');
  };

  const handleGenerateNewCode = async (promotion) => {
    if (!window.confirm('Generate a new code for this promotion?')) return;

    try {
      const data = await marketingService.generatePromoCode(promotion.id);
      showAlert(`New code generated: ${data.code}`);
      loadPromotions();
    } catch (error) {
      showAlert('Failed to generate code', 'error');
    }
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert({ ...alert, open: false }), 3000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDiscountText = (promotion) => {
    if (promotion.discount_type === 'percent') {
      return `${promotion.discount_value}% off`;
    } else {
      return `$${promotion.discount_value} off`;
    }
  };

  return (
    <Box>
      {alert.open && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
            }
            label="Show active only"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()}
            sx={{ height: '56px' }}
          >
            New Promotion
          </Button>
        </Grid>
      </Grid>

      <TableContainer component={Card}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Valid Period</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell>Min Purchase</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {promotions.map((promo) => (
              <TableRow key={promo.id}>
                <TableCell>{promo.name}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {promo.code}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyCode(promo.code)}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleGenerateNewCode(promo)}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={getDiscountText(promo)} size="small" color="secondary" />
                </TableCell>
                <TableCell>
                  {formatDate(promo.start_date)} - {formatDate(promo.end_date)}
                </TableCell>
                <TableCell>
                  {promo.used_count} / {promo.usage_limit || '∞'}
                  {promo.remaining !== null && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {promo.remaining} left
                    </Typography>
                  )}
                </TableCell>
                <TableCell>${promo.min_purchase || 0}</TableCell>
                <TableCell>
                  <Box>
                    <Chip
                      label={promo.is_valid ? 'Valid' : 'Invalid'}
                      color={promo.is_valid ? 'success' : 'default'}
                      size="small"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={promo.active}
                          onChange={() => handleToggleActive(promo)}
                          size="small"
                        />
                      }
                      label="Active"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenForm(promo)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeletePromotion(promo)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={formOpen}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPromotion ? 'Edit Promotion' : 'New Promotion'}
        </DialogTitle>
        <DialogContent>
          <PromotionForm
            promotion={selectedPromotion}
            onSave={handleSavePromotion}
            onCancel={handleCloseForm}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
